import TrendingDown from '@mui/icons-material/TrendingDown';
import TrendingUp from '@mui/icons-material/TrendingUp';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Stack } from '@mui/material';
import { getOperativeProfileData } from 'lib/api/business';
import { getRecieverFactoringRequestOverview } from 'lib/api/factoring';
import { formatMoney } from 'lib/functions/utils';
import type { KeyValueRowItem } from 'lib/mobileDesignSystem';
import {
  ContentCard,
  ErrorState,
  KeyValueCard,
  TabContentSkeleton,
} from 'lib/mobileDesignSystem';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export interface ProfilesProps {
  requestId: string;
}

interface OperativeProfileSummary {
  inFavorAccounts: Array<{ name: string; total: number }>;
  inFavorTotal: number;
  againstAccounts: Array<{ name: string; total: number }>;
  againstTotal: number;
  arrearsAccounts: Array<{ name: string; total: number }>;
  arrearsTotal: number;
}

function buildKeyValueItems(
  accounts: Array<{ name: string; total: number }>,
  total: number,
  filterPositive = false,
): KeyValueRowItem[] {
  const list = filterPositive ? accounts.filter((a) => a.total > 0) : accounts;
  const rows: KeyValueRowItem[] = list.map((a) => ({
    label: a.name,
    value: formatMoney(a.total),
  }));
  rows.push({ label: 'Total', value: formatMoney(total) });
  return rows;
}

const Profiles: React.FC<ProfilesProps> = ({ requestId }) => {
  const dispatch = useDispatch();
  const { data: operation, isLoading: overviewLoading } =
    getRecieverFactoringRequestOverview(dispatch, {
      requestId: requestId ?? '',
    });

  const businessIdentifier = operation?.businessIdentifier ?? null;

  const [profileData, setProfileData] =
    useState<OperativeProfileSummary | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!businessIdentifier) return;
    setProfileLoading(true);
    setProfileError(false);
    try {
      const response = await getOperativeProfileData(
        dispatch,
        businessIdentifier,
      );
      if (response) {
        setProfileData({
          inFavorAccounts: response.inFavorAccounts ?? [],
          inFavorTotal: response.inFavorTotal ?? 0,
          againstAccounts: response.againstAccounts ?? [],
          againstTotal: response.againstTotal ?? 0,
          arrearsAccounts: response.arrearsAccounts ?? [],
          arrearsTotal: response.arrearsTotal ?? 0,
        });
      } else {
        setProfileError(true);
      }
    } catch {
      setProfileError(true);
    } finally {
      setProfileLoading(false);
    }
  }, [dispatch, businessIdentifier]);

  useEffect(() => {
    if (businessIdentifier) {
      void fetchProfile();
    }
  }, [businessIdentifier, fetchProfile]);

  const loading = overviewLoading || profileLoading;
  const noBusinessId =
    !overviewLoading && operation !== undefined && !businessIdentifier;

  if (!requestId || requestId === '') {
    return <ErrorState message="Operación no válida." />;
  }

  if (overviewLoading && !operation) {
    return <TabContentSkeleton />;
  }

  if (noBusinessId) {
    return (
      <ErrorState message="No se pudo obtener el perfil para esta operación." />
    );
  }

  if (profileError) {
    return (
      <ErrorState message="Error al cargar el resumen de cuenta corriente." />
    );
  }

  if (loading && !profileData) {
    return <TabContentSkeleton />;
  }

  const data = profileData ?? {
    inFavorAccounts: [],
    inFavorTotal: 0,
    againstAccounts: [],
    againstTotal: 0,
    arrearsAccounts: [],
    arrearsTotal: 0,
  };

  const inFavorItems = buildKeyValueItems(
    data.inFavorAccounts,
    data.inFavorTotal,
    false,
  );
  const againstItems = buildKeyValueItems(
    data.againstAccounts,
    data.againstTotal,
    true,
  );
  const arrearsItems = buildKeyValueItems(
    data.arrearsAccounts,
    data.arrearsTotal,
    true,
  );

  return (
    <Stack spacing={2} sx={{ px: 2, pt: 2 }}>
      <ContentCard
        headerColor="success"
        header={{
          icon: <TrendingUp fontSize="small" color="success" />,
          title: 'Monto a Favor',
        }}
      >
        <KeyValueCard items={inFavorItems} highlightLastRows={1} />
      </ContentCard>

      <ContentCard
        headerColor="error"
        header={{
          icon: <TrendingDown fontSize="small" color="error" />,
          title: 'Monto en Contra',
        }}
      >
        <KeyValueCard items={againstItems} highlightLastRows={1} />
      </ContentCard>

      <ContentCard
        headerColor="warning"
        header={{
          icon: <WarningAmber fontSize="small" color="warning" />,
          title: 'Moras',
        }}
      >
        <KeyValueCard items={arrearsItems} highlightLastRows={1} />
      </ContentCard>
    </Stack>
  );
};

export default Profiles;
